import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  Save, 
  Loader2, 
  AlertCircle, 
  CheckCircle2, 
  Image as ImageIcon,
  Upload,
  X,
  Filter,
  RefreshCw,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';

const AdminGallery = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [filter, setFilter] = useState('ALL');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categories = [
    'ALL', 'ROOMS', 'POOL', 'KITCHEN', 'DINING', 'LIVING_SPACES', 
    'OUTDOORS', 'EXPERIENCES', 'BATHROOMS', 'OTHER'
  ];

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('gallery_items')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setMessage(null);

    try {
      for (const file of Array.from(files)) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('site-assets')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('site-assets')
          .getPublicUrl(filePath);

        const { error: dbError } = await supabase.from('gallery_items').insert([{
          url: publicUrl,
          category: filter === 'ALL' ? 'OTHER' : filter,
          alt: file.name.split('.')[0] || null,
          display_order: items.length,
          is_active: true
        }]);

        if (dbError) throw dbError;
      }

      setMessage({ type: 'success', text: 'Images uploaded successfully!' });
      fetchGallery();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: string, url: string) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      const fileName = url.split('/').pop();
      if (fileName) {
        await supabase.storage.from('site-assets').remove([fileName]);
      }

      const { error } = await supabase.from('gallery_items').delete().eq('id', id);
      if (error) throw error;

      setItems(prev => prev.filter(item => item.id !== id));
      setMessage({ type: 'success', text: 'Image deleted' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const updateItem = async (id: string, field: string, value: any) => {
    try {
      const updateData: any = {};
      updateData[field] = value;
      
      const { error } = await supabase
        .from('gallery_items')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;
      setItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const moveItem = async (id: string, direction: 'left' | 'right') => {
    const currentIndex = items.findIndex(i => i.id === id);
    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < items.length) {
      const newItems = [...items];
      [newItems[currentIndex], newItems[newIndex]] = [newItems[newIndex], newItems[currentIndex]];
      
      // Local state update
      const updatedItems = newItems.map((item, idx) => ({ ...item, display_order: idx }));
      setItems(updatedItems);
      
      // DB update
      try {
        await Promise.all([
          supabase.from('gallery_items').update({ display_order: currentIndex }).eq('id', newItems[currentIndex].id),
          supabase.from('gallery_items').update({ display_order: newIndex }).eq('id', newItems[newIndex].id)
        ]);
      } catch (error) {
        console.error('Error reordering:', error);
      }
    }
  };

  const filteredItems = filter === 'ALL' 
    ? items 
    : items.filter(item => item.category === filter);

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-6">
          <div>
            <h1 className="text-4xl font-serif text-emerald">Estate Gallery</h1>
            <p className="text-[#59635C] font-clean mt-2">Manage all property photographs.</p>
          </div>
          <div className="flex gap-4">
            <input
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-emerald text-ivory px-8 py-3 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/90 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload Photos
            </button>
          </div>
        </div>

        {message && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-clean font-medium">{message.text}</span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pb-4 border-b border-accent/10">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${
                filter === cat 
                  ? 'bg-emerald text-white' 
                  : 'bg-white text-emerald border border-emerald/20 hover:border-emerald'
              }`}
            >
              {cat.replace('_', ' ')}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-emerald" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence>
              {filteredItems.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-white rounded-2xl border border-accent/10 overflow-hidden shadow-sm group"
                >
                  <div className="aspect-[4/3] relative overflow-hidden bg-gray-100">
                    <img src={item.url} alt={item.alt || ''} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <div className="flex gap-1">
                        <button 
                          onClick={() => moveItem(item.id, 'left')}
                          disabled={items.indexOf(item) === 0}
                          className="p-2 bg-emerald text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-30"
                        >
                          <ArrowLeft className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => moveItem(item.id, 'right')}
                          disabled={items.indexOf(item) === items.length - 1}
                          className="p-2 bg-emerald text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-30"
                        >
                          <ArrowRight className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(item.id, item.url)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <select
                      value={item.category}
                      onChange={(e) => updateItem(item.id, 'category', e.target.value)}
                      className="w-full text-[10px] font-bold uppercase tracking-widest font-clean border-none bg-[#F5F1E8] rounded-md px-2 py-1 outline-none text-emerald"
                    >
                      {categories.filter(c => c !== 'ALL').map(c => (
                        <option key={c} value={c}>{c.replace('_', ' ')}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={item.alt || ''}
                      placeholder="Image caption..."
                      onChange={(e) => updateItem(item.id, 'alt', e.target.value)}
                      className="w-full text-xs font-clean border-none focus:ring-0 bg-transparent p-0 placeholder-gray-400"
                    />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {filteredItems.length === 0 && (
              <div className="col-span-full text-center py-20 bg-white rounded-2xl border-2 border-dashed border-accent/20">
                <ImageIcon className="w-12 h-12 text-accent/30 mx-auto mb-4" />
                <p className="text-[#59635C] font-clean">No images found in this category.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminGallery;
